import config from './config';
import URI from 'urijs';
import constants from './constants';
import { intersection } from 'lodash/array';
import { setAndGetLwaUniqId } from './cookie-utils';
import { logReloginEvent } from './amplitude-utils';
import sessionStorageUtil from './session-storage-util';
import { fetchLogoProps } from './logo-utils';
import { addHTI1Scopes } from './scope-mapping-utils';

export const parseAudForMetadata = (aud) => {
  try {
    let practiceId;
    let brandId;
    const match =
      aud.match(/(\d+)\/(\d+)\/\d+\/fhir\/dstu2$/) ||
      aud.match(/(\d+)\/brand\/(\d+)\/csg\/(\d+)\/fhir\/r4/) ||
      aud.match(/(\d+)\/(\d+)\/fhir\/r4/);
    if (match) {
      [, practiceId, brandId] = match;
    } else {
      return {};
    }
    const metadata = {};
    if (practiceId) {
      metadata.practiceId = practiceId;
    }
    // brand is set to 0 for athenaNet practices that don't have any brands
    // Ignore brand if it's not set or is a falsey-value.
    if (brandId && parseInt(brandId) !== 0) {
      metadata.brandId = brandId;
    }
    return metadata;
  } catch (e) {
    return {};
  }
};

const _extractScopes = (scopeString) => {
  return (scopeString && scopeString.split(/\s+/g)) || [];
};

/**
 * Derives the aud parameter from the resource parameter for MCP authorization flow (RFC8707)
 * Extracts the protocol, hostname, and port (if present) from the resource URL and appends /fhir/r4
 *
 * Examples:
 * - resource=https://api.preview.platform.athenahealth.com/mcp/patient
 *   returns https://api.preview.platform.athenahealth.com/fhir/r4
 * - resource=https://api.platform.athenahealth.com/v1/my-first-mcp
 *   returns https://api.platform.athenahealth.com/fhir/r4
 */
const _deriveAudFromResource = (resource) => {
  if (!resource) {
    return null;
  }
  try {
    const resourceUrl = new URL(resource);
    const port = resourceUrl.port ? `:${resourceUrl.port}` : '';
    const domain = `${resourceUrl.protocol}//${resourceUrl.hostname}${port}`;
    return `${domain}/fhir/r4`;
  } catch (e) {
    return null;
  }
};

const REGISTRATION_TOKEN_UUID_V4 =
  /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

// Extracts a registration_token carried through login_hint. Returns the UUID when login_hint is exactly
// a v4 UUID, otherwise null so a normal username/sentinel login_hint is left untouched.
export const extractRegistrationTokenFromLoginHint = (loginHint) => {
  if (typeof loginHint !== 'string') {
    return null;
  }
  const trimmed = loginHint.trim();
  return REGISTRATION_TOKEN_UUID_V4.test(trimmed) ? trimmed : null;
};

export const parsedPayload = (authorizeParams) => {
  const scopeString = authorizeParams.get('scope');
  const scopesArray = _extractScopes(scopeString);
  const redirectUrl = authorizeParams.get('redirect_uri');
  const clientId = authorizeParams.get('client_id');
  const state = authorizeParams.get('state');
  const resource = authorizeParams.get('resource');
  // If aud is not provided but resource is, derive aud from resource (MCP flow)
  let aud = authorizeParams.get('aud');
  if (!aud && resource) {
    aud = _deriveAudFromResource(resource);
  }
  const responseType = authorizeParams.get('response_type');
  const prompt = authorizeParams.get('prompt');
  const createAccount = authorizeParams.has('create');
  const launch = authorizeParams.get('launch');
  const idp = authorizeParams.get('idp');
  const sessionToken = authorizeParams.get('sessionToken');
  const relogin = authorizeParams.get('relogin');
  const testApp = authorizeParams.get('testApp');
  const rawLoginHint = authorizeParams.get('login_hint');
  const loginHintToken = extractRegistrationTokenFromLoginHint(rawLoginHint);
  // If login_hint is actually carrying a registration_token,
  // it must NOT be forwarded to Okta as a username.
  const username = loginHintToken ? null : rawLoginHint;
  const uiLocales = authorizeParams.get('ui_locales');
  const userId = authorizeParams.get('amplitude_user_id');
  const amplitudeDeviceId = authorizeParams.get('amplitude_device_id');
  const errorDetails = authorizeParams.get('error_details');

  let errorPayloadFromOhsw = null;
  if (errorDetails) {
    try {
      errorPayloadFromOhsw = JSON.parse(atob(errorDetails));
    } catch (e) {
    }
  }

  let emailToken;
  if (authorizeParams.get('registration_token')) {
    emailToken = authorizeParams.get('registration_token');
  } else if (authorizeParams.get('email_token')) {
    emailToken = authorizeParams.get('email_token');
  } else if (authorizeParams.get('emailToken')) {
    emailToken = authorizeParams.get('emailToken');
  } else if (authorizeParams.get('registrationToken')) {
    emailToken = authorizeParams.get('registrationToken');
  } else if (loginHintToken) {
    emailToken = loginHintToken;
  }

  let skipPatientSummary = false;
  const skipPatientSummaryValue =
    authorizeParams.get('skip_patient_summary') ||
    authorizeParams.get('skipPatientSummary');
  if (skipPatientSummaryValue !== null) {
    skipPatientSummary = skipPatientSummaryValue === 'true';
  }
  let hideRememberMe = false;
  const hideRememberMeValue =
    authorizeParams.get('hide_remember_me') ||
    authorizeParams.get('hideRememberMe');
  if (hideRememberMeValue !== null) {
    hideRememberMe = hideRememberMeValue === 'true';
  }

  let tempPassHint = false;
  const tempPassHintValue = authorizeParams.get('temp_pass_hint');
  if (tempPassHintValue !== null) {
    tempPassHint = tempPassHintValue === 'patient';
  }

  const fhir = isFhirScopeRequested(scopesArray);
  function isFhirScopeRequested(scopes) {
    return intersection(config.fhirScopes, scopes).length > 0;
  }

  function isFhirEhrLaunchRequested(scopes) {
    return intersection(config.fhirEhrLaunchScopes, scopes).length > 0;
  }
  const isFhirEhrLaunch = isFhirEhrLaunchRequested(scopesArray) && !!launch;
  const authorizeRedirectUrl = new URI(window.location.href).origin(
    config.oktaURL
  );
  const originalRedirectUri = new URI(window.location.href)
    .removeQuery('prompt', 'login')
    .removeQuery('relogin');

  // If aud was derived from resource parameter (MCP flow), add it to originalRedirectUri
  if (aud && resource && !originalRedirectUri.hasQuery('aud')) {
    originalRedirectUri.addQuery({ aud });
  }

  const authServerId = authorizeRedirectUrl.segment()[1];
  const lwaUniqId = setAndGetLwaUniqId();

  return {
    clientId: clientId,
    scopesArray,
    scopeString,
    redirectUri: redirectUrl,
    resource,
    aud,
    prompt,
    emailToken,
    launch,
    createAccount,
    isFhirEhrLaunch,
    fhir,
    idp,
    authorizeRedirectUrl,
    sessionToken,
    relogin,
    testApp,
    authServerId,
    responseType,
    username,
    state,
    lwaUniqId,
    userId,
    amplitudeDeviceId,
    originalRedirectUri,
    uiLocales,
    skipPatientSummary,
    hideRememberMe,
    tempPassHint,
    errorPayloadFromOhsw,
  };
};

const _getWorkflow = ({ idp, fhir }) => {
  if (idp) {
    return constants.AUTHN_WORKFLOWS.AUTHORIZEVIAIDP;
  } else {
    if (fhir) {
      return constants.AUTHN_WORKFLOWS.FHIRAUTHORIZE;
    } else {
      return constants.AUTHN_WORKFLOWS.AUTHORIZE;
    }
  }
};

export const getProxiedAuthorizeValues = ({
  parsedPayload = {},
  apiResponse = {},
  preAuthnRequestPayload  = {},
}) => {
  const {
    fhir,
    sessionToken,
    testApp,
    authServerId,
    originalRedirectUri,
    oauthCodeVerifier,
    oauthCodeChallenge,
  } = parsedPayload;
  const idp = apiResponse.idp || parsedPayload.idp;
  const workflow = _getWorkflow({ idp, fhir });

  return {
    workflow,
    originalRedirectUri: originalRedirectUri.toString(),
    fhir,
    sessionToken,
    testApp,
    authServerId,
    idp,
    prompt: parsedPayload.prompt,
    profile: false,
    // eslint-disable-next-line camelcase
    login_hint: apiResponse.username || parsedPayload.username,
    oauthCodeVerifier,
    oauthCodeChallenge,
    ...(Object.keys(preAuthnRequestPayload).length > 0
      ? { preAuthnRequestPayload: btoa(JSON.stringify(preAuthnRequestPayload)) }
      : {}
    ),
  };
};

export function enrichApiResponse({ apiResponse, parsedPayload }) {
  const {
    authorizeRedirectUrl,
    createAccount,
    clientId,
    aud,
    state,
    username,
    skipPatientSummary,
    hideRememberMe,
    tempPassHint,
    authorizePostParams,
    resource,
  } = parsedPayload;
  const idp = apiResponse.idp || parsedPayload.idp;
  const prompt = idp ? undefined : parsedPayload.prompt;
  const proxiedAuthorizeValues = getProxiedAuthorizeValues({
    parsedPayload,
    apiResponse,
  });

  // If aud was derived from resource parameter (MCP flow), add it to the URL
  if (aud && resource && !authorizeRedirectUrl.hasQuery('aud')) {
    authorizeRedirectUrl.addQuery({ aud });
  }

  // for non-federated cases, we provide our own login experience, so we override some parameters related to that,
  // but for the federated case we want to let Okta handle login prompting behavior
  idp
    ? authorizeRedirectUrl.removeQuery('prompt', 'login').addQuery({ idp })
    : authorizeRedirectUrl
      .removeQuery('prompt', 'login')
      .removeQuery('login_hint');
  const postAuthnRoutes = JSON.stringify(
    apiResponse.postProcessingSteps
      .map((step) => {
        if (step.needsSanitize) {
          return { route: 'account/sanitize' };
        }
        if (step.workflow) {
          return { route: 'workflow' };
        }
        if (step.authorizeToOkta) {
          return { url: authorizeRedirectUrl.toString() };
        }
      })
      .filter((ele) => !!ele)
  );
  return {
    ...apiResponse,
    idp,
    postAuthnRoutes,
    proxiedAuthorizeValues: JSON.stringify(proxiedAuthorizeValues),
    createAccount,
    clientId,
    aud,
    state,
    prompt,
    username: apiResponse.username || username,
    workflow: proxiedAuthorizeValues.workflow,
    skipPatientSummary,
    hideRememberMe,
    tempPasswordHint: tempPassHint,
    ...fetchLogoProps(idp),
    authorizePostParams,
  };
}

/**
 * Check if relogin attempted already, attempts if not
 * @param cause {string} - Reason for performing the relogin, so it gets logged to amplitude
 * @return {Promise<boolean>} Indicates if the relogin is being performed
 */
export async function performReloginIfAllowed({ cause, message, originalRedirectUri }) {
  const originalAuthUrl = sessionStorageUtil.getOriginalAuthUrl() || originalRedirectUri;
  if (originalAuthUrl) {
    const parsedUrl = new URL(originalAuthUrl);
    if (!parsedUrl.searchParams.get('relogin')) {
      parsedUrl.searchParams.append('relogin', '1');
      logReloginEvent({ cause, message });
      window.location.replace(parsedUrl.toString());
      return true;
    }
  }
  return false;
}

/**
 * Get username from session if exist
 * @param {authClient} OktaAuth from @okta/okta-react
 * @returns {Promise<undefined|string>}
 */
export async function getUsernameFromSession(oktaAuth) {
  const { login } = await oktaAuth.session.get();
  return login;
}

export function showPrimaryAuth() {
  const currentHash = window.location.hash.replace(/^#/, '');
  // Some pages shouldn't go to the login page.
  const noLogins = ['create', 'profile', 'signin/athena-forgot-password'];
  document.getElementById(constants.LOGIN_CONTAINER_ID).style.display = 'block';
  if (noLogins.indexOf(currentHash) >= 0) {
    return;
  }
  if (currentHash !== '') {
    window.location.hash = '';
  }
}

const _getPromptForLwaSpaOhswWorkflow = (prompt) => {
  // with IDP we don't want to prompt for consent for the lwa spa client
  if (prompt === 'consent') {
    return '';
  } else {
    return prompt || '';
  }
};

export const getPromptForLwaSpa = ({ prompt, idp, hasPreAuthnRequestPayload }) => {
  if (idp) {
    return _getPromptForLwaSpaOhswWorkflow(prompt);
  } else {
    if (hasPreAuthnRequestPayload) {
      return _getPromptForLwaSpaOhswWorkflow(prompt);
    } else {
      // We just authenticated the user by forcing showPrimaryAuth for non-idp
      return '';
    }
  }
};

export const getAuthorizeToOktaValues = ({ proxiedAuthorizeValues }) => {
  const values = Object.assign({}, proxiedAuthorizeValues);
  const loginHint = values.login_hint;
  const sessionToken = values.sessionToken;
  delete values.profile;
  delete values.sessionToken;
  const codeVerifier = values.oauthCodeVerifier;
  const codeChallenge = values.oauthCodeChallenge;
  delete values.oauthCodeVerifier;
  delete values.oauthCodeChallenge;
  const { idp, prompt } = values;

  const hasPreAuthnRequestPayload = !!proxiedAuthorizeValues.preAuthnRequestPayload;
  const finalPrompt = getPromptForLwaSpa({ prompt, idp, hasPreAuthnRequestPayload });
  const authorizeParams = {
    response_type: 'code',
    client_id: config.oauthClientId,
    prompt: finalPrompt,
    state: JSON.stringify(values),
    idp,
    sessionToken,
    loginHint: (finalPrompt === 'login' && hasPreAuthnRequestPayload && !idp)
      ? (loginHint || 'ANETUSERNAME')
      : loginHint,
    codeChallenge,
    codeVerifier,
    isPostAuthorize: Object.keys(getPostAuthorizeParams()).length > 0,
  };
  return authorizeParams;
};

const appendHiddenInput = (form, name, value) => {
  const input = document.createElement('input');
  input.type = 'hidden';
  input.name = name;
  input.value = value;
  form.appendChild(input);
};
/**
 * posts auhtorize to okta
 * @param {string} - Use these values to make a POST request
 * @return {form post} form post request
 */
export const postAuthorizePostForm = async ({
  oktaAuth,
  idp,
  prompt,
  sessionToken,
  state,
  loginHint,
  codeVerifier,
  codeChallenge,
}) => {

  sessionStorage.setItem('okta-authorize-url', `${oktaAuth.options.issuer}/v1/authorize`);
  const form = document.createElement('form');
  form.method = 'post';
  form.action = `${oktaAuth.options.issuer}/v1/authorize`;

  appendHiddenInput(form, 'client_id', config.oauthClientId);
  appendHiddenInput(form, 'redirect_uri', `${window.location.origin}/${config.redirectUri}`);
  appendHiddenInput(form, 'response_type', 'code');
  appendHiddenInput(form, 'state', state);
  appendHiddenInput(form, 'scope', config.scopes.join(' '));
  appendHiddenInput(form, 'code_challenge', codeChallenge);
  appendHiddenInput(form, 'code_challenge_method', 'S256');
  appendHiddenInput(form, 'response_mode', 'form_post');
  appendHiddenInput(form, 'idp', idp || '');
  appendHiddenInput(form, 'prompt', prompt);
  appendHiddenInput(form, 'sessionToken', sessionToken);
  appendHiddenInput(form, 'login_hint', loginHint || '');

  oktaAuth.transactionManager.save({
    clientId: config.oauthClientId,
    redirectUri: `${window.location.origin}/${config.redirectUri}`,
    responseType: 'code',
    scope: config.scopes.join(' '),
    codeChallenge,
    codeChallengeMethod: 'S256',
    codeVerifier,
    responseMode: 'form_post',
    state,
  });

  document.body.appendChild(form);
  return form.submit();
};

export const authorizeToOkta = async ({ proxiedAuthorizeValues, oktaAuth }) => {

  const { preAuthnRequestPayload } = proxiedAuthorizeValues;

  const authorizeParams = getAuthorizeToOktaValues({
    proxiedAuthorizeValues,
  });

  const {
    idp,
    prompt,
    sessionToken,
    state,
    loginHint,
    codeVerifier,
    codeChallenge,
    isPostAuthorize,
  } = authorizeParams;

  if (isPostAuthorize) {
    const codeVerifierFinal = codeVerifier ?? oktaAuth.pkce.generateVerifier();
    const codeChallengeFinal = codeChallenge ?? await oktaAuth.pkce.computeChallenge(codeVerifierFinal);

    const authorizePostParamsObject = getPostAuthorizeParams();
    if (Object.keys(authorizePostParamsObject).length > 0) {
      authorizePostParamsObject.scope = addHTI1Scopes(authorizePostParamsObject.scope);
    }
    return postAuthorizePostForm({
      oktaAuth,
      idp,
      prompt,
      sessionToken,
      state: JSON.stringify({ authorizePostParamsObject, preAuthnRequestPayload, postToOkta: true }),
      loginHint,
      codeVerifier: codeVerifierFinal,
      codeChallenge: codeChallengeFinal,
    });
  } else {
    return oktaAuth.signInWithRedirect({
      idp,
      prompt,
      state,
      sessionToken,
      originalUri: window.location.href,
      loginHint,
      codeVerifier,
      codeChallenge,
    });
  }
};

/**
 * gets authorize param valies from index.html
 * @param {string} - Use these values to make a POST request
 * @return {form post} form post request
 */
export const getPostAuthorizeParams = () => {
  const scriptElement = document.getElementById('authorize-params');
  if (!scriptElement) {
    return {};
  }
  const jsonString = scriptElement.textContent || scriptElement.innerText;
  if (!jsonString) {
    return {};
  }
  const jsonObject = JSON.parse(jsonString);
  return jsonObject;
};

const OKTA_POST_AUTHORIZE_REQUIRED_PARAMS = [
  'response_type',
  'client_id',
  'redirect_uri',
  'scope',
  'state',
  "code_challenge",
  'code_challenge_method',
  'response_mode',
  'idp',
  'prompt',
  'sessionToken',
  'login_hint',
  'nonce',
];

const addQueryParamToUrl = (url, key, value) => {
  const queryString = `${key}=${encodeURIComponent(value)}`;
  const separator = url.includes('?') ? '&' : '?';
  return `${url}${separator}${queryString}`;
};
/**
 * submits original authorize to okta
 * @param cause {string} - Use these values to make a POST request
 * @return {authorizePostParams} authorize params to be used in the request
 */
export const postAuthorizePostFhirForm = ({
  originalPostAuthorizationParams,
  authorizeToOktaUrl,
  isLoginCallBackError = false,
}) => {
  const form = document.createElement('form');
  form.method = 'post';
  for (const key in originalPostAuthorizationParams) {
    if (
      (OKTA_POST_AUTHORIZE_REQUIRED_PARAMS.includes(key) || isLoginCallBackError) &&
      typeof originalPostAuthorizationParams[key] === 'string'
    ) {
      appendHiddenInput(form, key, originalPostAuthorizationParams[key]);
    } else {
      authorizeToOktaUrl = addQueryParamToUrl(authorizeToOktaUrl, key, originalPostAuthorizationParams[key]);
    }
  }
  document.body.appendChild(form);
  form.action = authorizeToOktaUrl + (isLoginCallBackError ? '?relogin=1' : "");
  return form.submit();
};

function _isFhirPatientScopeRequested(scopes) {
  return intersection(config.fhirPatientScopes, scopes).length > 0;
}

export const isFhirRequest = () => {
  const postFormParams = getPostAuthorizeParams();
  if (Object.keys(postFormParams).length > 0) {
    try {
      const parsedState = JSON.parse(postFormParams.state);
      if (parsedState && parsedState.authorizePostParamsObject) {
        const requestedScopes = _extractScopes(parsedState.authorizePostParamsObject.scope);
        return _isFhirPatientScopeRequested(requestedScopes);
      }
    } catch (e) {
      //ignore
    }
  }
  const originalAuthUrl = sessionStorageUtil.getOriginalAuthUrl();
  if (originalAuthUrl) {
    const authUrl = new URL(originalAuthUrl);
    const requestedScopesString = authUrl.searchParams.get('scope');
    const requestedScopes = _extractScopes(requestedScopesString);
    return _isFhirPatientScopeRequested(requestedScopes);
  }
  return false;
};
